import React, { useState, useEffect } from "react";
import ReactPlayer from "react-player";
import { FiEye, FiChevronDown, FiChevronRight } from "react-icons/fi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";
import { Box, Typography, Modal, CircularProgress, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";

import styles from "./confirmAndSend.module.scss";
import ConsentBundelPlaylistModel from "../../VideoManager/ConsentBundelPlaylistModel";
import SelectFromStockVideoModal from "../../VideoManager/SelectFromStockVideoModal";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";


export default function ConfirmAndSend({ onNext, onBack, treatmentData, setTreatmentData }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [expanded, setExpanded] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  // Modals
  const [youtubeModalOpen, setYoutubeModalOpen] = useState(false);
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [selectedListId, setSelectedListId] = useState(null);
  const [s3Videos, setS3Videos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [clickedVideoId, setClickedVideoId] = useState(null);

  /* ---------------- Load playlists ---------------- */
  useEffect(() => {
    if (treatmentData?.playlists?.length) {
      const formatted = treatmentData.playlists.map((pl) => ({
        id: pl.id,
        title: pl.title,
        status: pl.status,   
        videos: pl.playlist_items.map((v) => {
          const youtubeId = extractYouTubeId(v.url);
          return {
            id: v.id,
            title: v.title,
            url: v.url,
            status: v.status,
            type: youtubeId ? "youtube" : "stock",
            thumb:
              v.thumbnail ||
              (youtubeId
                ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`
                : "/assets/video-placeholder.png"),
          };
        }),
      }));

      setPlaylists(formatted);

      const firstVideo = formatted[0]?.videos?.[0];
      if (firstVideo)
        setSelectedVideo({
          title: firstVideo.title,
          url: firstVideo.url,
          type: firstVideo.type,
        });

      if (!expanded && formatted[0]?.id) setExpanded(formatted[0].id);
    } else {
      setPlaylists([]);
      setSelectedVideo(null);
      setExpanded(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentData]);

  const extractYouTubeId = (url) => {
    try {
      const match = url.match(
        /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/
      );
      return match ? match[1] : null;
    } catch {
      return null;
    }
  };

  const handleVideoSelect = (url, title) => {
    const youtubeId = extractYouTubeId(url);
    setSelectedVideo({
      title,
      url,
      type: youtubeId ? "youtube" : "stock",
    });
  };

  const togglePlaylist = (id) => setExpanded(expanded === id ? null : id);

  /* ---------------- Reload treatment ---------------- */
  const reloadTreatmentData = async () => {
    if (!treatmentData?.id) return;
    try {
      setIsReloading(true);
      const res = await axios.get(`/consent_bundle/${treatmentData.id}`);
      if (res?.data) {
        setTreatmentData(res.data);
    
      }
    } catch {
      toast.error("Failed to reload updated data.");
    } finally {
      setIsReloading(false);
    }
  };

  /* ---------------- Send for signing ---------------- */
  const sendForSigning = useMutation(
    async () => {
      if (!treatmentData?.id) throw new Error("Missing bundle ID");
      return axios.post(
        `/consent_bundle/${treatmentData.id}/send_consent_bundle_for_signing`,
        {}
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["treatmentDetails"] });
        window.location.href = `/sendConsent/${treatmentData.id}`;
      },
      onError: () => toast.error("Failed to send for signing. Please try again."),
    }
  );

  const handleSendConsent = () => {
    if (treatmentData?.version_no >= 2) {
      setShowWarning(true);
      return;
    }
    proceedSendConsent();
  };

  const proceedSendConsent = async () => {
    try {
      setUploading(true);
      await sendForSigning.mutateAsync();
    } catch (e) {
      console.error(e);
    } finally {
      setUploading(false);
      setShowWarning(false);
    }
  };

  const loadS3Videos = async () => {
    try {
      const response = await axios.get(`/playlist/global/get_global_playlist_items`);
      setS3Videos(response.data);
    } catch {
      toast.error("Failed to fetch stock videos.");
    }
  };

  const toggleVideoVisibility = useMutation(
  (videoId) => {
    return axios.put(
      `/consent_bundle/${treatmentData.id}/toggle_visibility_playlist_item/${videoId}`
    );
  },
  {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["treatmentDetails"] });
      reloadTreatmentData();
    },
    onError: () => toast.error("Failed to update video visibility."),
  }
);

const handleToggleVisibility = (videoId) => {
  setClickedVideoId(videoId);
  toggleVideoVisibility.mutate(videoId);
};


const deletePlaylist = useMutation(
  (playlistId) => {
    return axios.put(
      `/consent_bundle/${treatmentData.id}/toggle_visibility_playlist/${playlistId}`
    );
  },
  {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["treatmentDetails"] });
      reloadTreatmentData();
    },
    onError: () => toast.error("Failed to update playlist visibility."),
  }
);

const handleTogglePlaylistVisibility = (playlistId) => {
  deletePlaylist.mutate(playlistId);
};


  return (
    <div className={styles.page}>
      <div className={styles.mainContent}>
        {/* ---------- LEFT: Video Section ---------- */}
        <section className={styles.videoSection}>
          <h2 className={styles.videoTitle}>
            {selectedVideo ? selectedVideo.title : "Select a video to preview"}
          </h2>
          <p className={styles.videoSubtitle}>
            {selectedVideo ? "Selected video" : ""}
          </p>

          <div className={styles.videoWrapper}>
            {selectedVideo ? (
              <ReactPlayer
                url={selectedVideo.url}
                width="100%"
                height="100%"
                controls
                playing={false}
                config={{
                  youtube: { playerVars: { modestbranding: 1, rel: 0 } },
                  file: {
                    attributes: { controlsList: "nodownload" },
                  },
                }}
              />
            ) : (
              <div className={styles.videoEmpty}>No video selected</div>
            )}
          </div>
          {isReloading && <p className={styles.refreshMsg}>🔄 Refreshing data...</p>}
        </section>

        {/* ---------- RIGHT: Sidebar ---------- */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <div className={styles.sidebarTitleRow}>
              <h3>Playlists</h3>
              <span className={styles.listCount}>{playlists.length} lists</span>
            </div>
           <div className={styles.btnRow}>
              <button
                className={styles.youtubeBtn}
                onClick={() => {
                  const firstList = playlists[0];
                  setSelectedListId(firstList ? firstList.id : null);
                  setYoutubeModalOpen(true);
                }}
              >
                <span className={styles.plusIcon}>+</span>
                Add from YouTube
              </button>

              <button
                className={styles.stockBtn}
                onClick={() => {
                  loadS3Videos();
                  setSelectedListId(playlists[0]?.id || null);
                  setStockModalOpen(true);
                }}
              >
                <span className={styles.plusIconOrange}>+</span>
                Add stock video
              </button>
            </div>


          </div>

          {playlists.length === 0 ? (
            <p className={styles.emptyText}>No playlists found.</p>
          ) : (
            playlists.map((pl) => {
              const isOpen = expanded === pl.id;
              return (
                <div
                  key={pl.id}
                  className={`${styles.playlistAccordion} ${isOpen ? styles.active : ""}`}
                >
                  <div
                      className={styles.accordionHeader}
                      onClick={() => togglePlaylist(pl.id)}
                    >
                      <strong>{pl.title}</strong>

                      <div className={styles.accordionMeta}>
                        <span className={styles.badge}>{pl.videos.length}</span>

                       
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePlaylistVisibility(pl.id);
                          }}
                          style={{ cursor: "pointer", marginRight: "8px" }}
                          title={pl.status ? "Hide playlist" : "Unhide playlist"}
                        >
                          {pl.status ? (
                            <VisibilityIcon sx={{ fontSize: 18, color: "#555" }} />
                          ) : (
                            <VisibilityOffIcon sx={{ fontSize: 18, color: "red" }} />
                          )}
                        </span>

                        {/* Accordion arrow */}
                        {isOpen ? <FiChevronDown /> : <FiChevronRight />}
                      </div>
                    </div>



                  {/* playlist iteam */}
                  <div
                    className={`${styles.accordionContent} ${isOpen ? styles.open : ""}`}
                  >
                    <div className={styles.accordionInner}>
                      {pl.videos.map((v) => (
                        <div
                          key={v.id}
                          className={styles.accordionVideo}
                          onClick={() => handleVideoSelect(v.url, v.title)}
                        >
                          <div className={styles.videoInfo}>
                            <img src={v.thumb} alt={v.title} />
                            <span className={styles.accordionVideoTitle}>{v.title}</span>
                          </div>

                          <div className={styles.iconRow}>
                          
                            {v.status ? (
                              <VisibilityIcon
                                fontSize="small"
                                title="Hide video"
                                sx={{ cursor: "pointer", marginLeft: "8px", color: "#555" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleVisibility(v.id);
                                }}
                              />
                            ) : (
                              <VisibilityOffIcon
                                fontSize="small"
                                title="Unhide video"
                                sx={{ cursor: "pointer", marginLeft: "8px", color: "red" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleVisibility(v.id);
                                }}
                              />
                            )}
                          </div>
                        </div>


                      ))}
                    </div>
                  </div>
                </div>
              );
            })
          )}

          <div className={styles.footerActions}>
            <button className={styles.backBtn} onClick={onBack}>
              ← Back
            </button>
            <button className={styles.sendBtn} onClick={handleSendConsent}>
              🚀 Send Consent
            </button>
          </div>
        </aside>
      </div>

      {/* ---------- Upload Modal ---------- */}
      <Modal open={uploading}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: "12px",
            boxShadow: 24,
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress size={50} thickness={4} />
          <Typography sx={{ mt: 2, fontWeight: 600 }}>
            {sendForSigning.isLoading ? "Sending consent..." : "Uploading..."}
          </Typography>
        </Box>
      </Modal>

      {/* ---------- Warning Modal ---------- */}
      <Modal open={showWarning} onClose={() => setShowWarning(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            minWidth: 360,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Warning
          </Typography>
          <Typography sx={{ mb: 3, color: "#555" }}>
            Your patient will lose access to the previous version. <br />
            Would you like to continue?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
            <Button onClick={() => setShowWarning(false)} sx={{ color: "orange", fontWeight: 600 }}>
              NO
            </Button>
            <Button onClick={proceedSendConsent} sx={{ color: "orange", fontWeight: 600 }}>
              YES
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ---------- YouTube & Stock Modals ---------- */}
      <ConsentBundelPlaylistModel
        open={youtubeModalOpen}
        onClose={() => setYoutubeModalOpen(false)}
        listId={selectedListId}
        consentBundleId={treatmentData?.id || null}
        onSuccess={() => {
          setUploading(false);
          setYoutubeModalOpen(false);
          reloadTreatmentData();
        }}
        onUploadStart={() => setUploading(true)}
      />

      <SelectFromStockVideoModal
        open={stockModalOpen}
        onClose={() => setStockModalOpen(false)}
        s3Videos={s3Videos}
        selectedVideos={selectedVideos}
        setSelectedVideos={setSelectedVideos}
        hoveredVideo={hoveredVideo}
        setHoveredVideo={setHoveredVideo}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        consentBundleId={treatmentData?.id || ""}
        onSuccess={() => {
          setUploading(false);
          setStockModalOpen(false);
          reloadTreatmentData();
        }}
        onUploadStart={() => setUploading(true)}
      />
    </div>
  );
}
